import React, { useCallback, useEffect, useState } from "react";
import { Tab, TabList, Tabs, Modal, ModalDialog, Textarea, CircularProgress } from "@mui/joy";
import { ArrowUpward, HelpOutline } from "@mui/icons-material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getFetch, postFetch } from "../../../../fetch/fetch";
import styles from "./faqadmin.module.css";
import { formatShortDateTime, formatNumber, toastOptions } from "./faqutil";
import FaqItems from "./faqitems";
import FaqEditDrawer from "./faqeditdrawer";
import FaqReview from "./faqreview";
import FaqStats from "./faqstats";

/**
 * 본사직원페이지 > 'FAQ 관리' 탭 (스펙 7.2) — 소탭 3개: FAQ 관리 / 검토 큐 / 통계
 *  - 상단 배포 상태 박스: 현재 배포 vN · 시각 · 항목 수 · 토큰 + '미배포 변경 N건' 배지 + [배포하기]
 *    (배포 = 서버가 faq_item(active) 전문을 prompt 로 묶어 count_tokens 측정 후 스냅샷 생성. 그 전까지 수정은 직원 화면에 안 나감. Haiku 일 때만 100K 초과 단가 5배)
 *  - 편집 드로어는 여기서 하나만 두고 FAQ 관리(행 클릭/새 FAQ)·검토 큐(FAQ 수정/새 FAQ 추가) 가 공유한다.
 *    검토 큐에서 열린 드로어로 저장하면 서버가 해당 피드백을 resolved 처리 (feedbackId 동봉), 묶음(×N) 의 나머지는 여기서 resolve 호출
 *  서버: /api/faq/* (users.todoistApiKey 보유자 전용)
 */

export interface DrawerState {
    open : boolean;
    mode : "edit" | "new";
    itemId? : number;
    prefill? : { question? : string, category? : string };
    feedbackIds? : number[];
}

const FaqAdminMain : React.FC<any> = (props : any) => {

    const [sub, setSub] = useState<number>(0);
    const [releaseInfo, setReleaseInfo] = useState<any>(null);
    const [reviewCount, setReviewCount] = useState<number>(0);
    const [categories, setCategories] = useState<any[]>([]);
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const [drawer, setDrawer] = useState<DrawerState>({ open : false, mode : "new" });

    const [deployOpen, setDeployOpen] = useState(false);
    const [deployMemo, setDeployMemo] = useState("");
    const [deploying, setDeploying] = useState(false);

    const loadReleases = useCallback(async () => {
        try {
            const result : any = await getFetch("/api/faq/releases");
            if (result && result.message === "success") {
                setReleaseInfo(result);
            } else if (result && result.message === "NOT_AUTHORIZED") {
                toast.error("권한이 없습니다 (todoistApiKey 필요)", toastOptions);
            }
        } catch (e) {
            console.log(e);
        }
    }, []);

    const loadCategories = useCallback(async () => {
        try {
            const result : any = await getFetch("/api/faq/categories");
            if (result && result.message === "success") {
                setCategories(result.categories);
            }
        } catch (e) {
            console.log(e);
        }
    }, []);

    const loadReviewCount = useCallback(async () => {
        try {
            const result : any = await getFetch("/api/faq/review?status=open");
            if (result && result.message === "success") {
                setReviewCount(result.counts ? +result.counts.total : 0);
            }
        } catch (e) {
            console.log(e);
        }
    }, []);

    useEffect(() => {
        loadReleases();
        loadCategories();
        loadReviewCount();
    }, [refreshKey, loadReleases, loadCategories, loadReviewCount]);

    const bumpRefresh = () => setRefreshKey((k) => k + 1);

    const openEditor = (state : Omit<DrawerState, "open">) => {
        setDrawer({ ...state, open : true });
    };

    // 드로어 저장 완료 — 묶음 피드백의 나머지도 resolved 처리
    const handleSaved = async (itemId : number, mode : "edit" | "new") => {
        const ids = drawer.feedbackIds || [];
        if (ids.length > 1) {
            const rest = ids.slice(1);
            for (const fid of rest) {
                try {
                    await postFetch(`/api/faq/review/${fid}/resolve`, { status : "resolved", resolutionNote : mode === "new" ? `#${itemId} 신규 추가` : `FAQ #${itemId} 수정함`, resolvedItemId : itemId });
                } catch (e) {
                    console.log(e);
                }
            }
        }
        setDrawer((d) => ({ ...d, open : false }));
        bumpRefresh();
    };

    const deploy = async () => {
        if (deploying) {
            return;
        }
        setDeploying(true);
        try {
            const result : any = await postFetch("/api/faq/releases", { memo : deployMemo.trim() });
            if (result && result.message === "success") {
                const priceText = result.priceMultiplier > 1 ? `단가 ${result.priceMultiplier}배` : `질문당 약 $${(+result.estCostPerQuestionUsd).toFixed(4)}`;
                toast.success(`v${result.version} 배포 완료 · ${formatNumber(result.promptTokens)} tokens · ${result.model} · ${priceText}`, { ...toastOptions, autoClose : 6000 });
                setDeployOpen(false);
                setDeployMemo("");
                bumpRefresh();
            } else if (result && result.message === "BUILDING") {
                toast.warning("이미 배포가 진행 중입니다", toastOptions);
            } else if (result && result.message === "TOKEN_LIMIT_EXCEEDED") {
                toast.error(result.error || "토큰 한도를 넘어 배포할 수 없습니다", { ...toastOptions, autoClose : 8000 });
            } else {
                toast.error((result && result.error) || "배포에 실패했습니다", toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("배포 요청에 실패했습니다 (시간 초과일 수 있음 — 잠시 후 새로고침해 확인)", { ...toastOptions, autoClose : 6000 });
        } finally {
            setDeploying(false);
        }
    };

    const current = releaseInfo ? releaseInfo.current : null;
    const pending : number = releaseInfo ? +releaseInfo.pendingChanges : 0;

    return (
        <div className={styles.wrap}>
            <div className={styles.headerRow}>
                <div className={styles.title}>
                    <HelpOutline sx={{ fontSize : 24 }} />
                    FAQ 관리
                </div>
                <div className={styles.deployBox}>
                    {
                        current ?
                            <span><b>현재 배포 v{current.version}</b> · {formatShortDateTime(current.createdAt)} · {formatNumber(current.itemCount)}항목 · {formatNumber(current.promptTokens)} tokens{current.priceMultiplier > 1 ? ` · 단가 ${current.priceMultiplier}배` : ""}{releaseInfo.model ? ` · ${releaseInfo.model}` : ""}</span>
                            :
                            <span><b>배포 없음</b> — 배포하기를 눌러야 직원 챗이 동작합니다</span>
                    }
                    {
                        pending > 0 ?
                            <span className={`${styles.badge} ${styles.badgeAmber}`}>미배포 변경 {formatNumber(pending)}건</span>
                            :
                            <span className={`${styles.badge} ${styles.badgeGray}`}>미배포 변경 없음</span>
                    }
                    <button className={`${styles.btn} ${styles.btnPrimary}`} disabled={pending === 0 || (releaseInfo && releaseInfo.building)} onClick={() => setDeployOpen(true)}>
                        <ArrowUpward sx={{ fontSize : 16 }} /> 배포하기
                    </button>
                </div>
            </div>

            {/* 상위 탭(main.tsx) 과 구분되도록 작은 Joy Tabs (winterregistermain.tsx 와 같은 패턴) */}
            <Tabs size="sm" value={sub} onChange={(e : any, value : any) => setSub(+value)} sx={{ width : "100%", marginTop : "16px", backgroundColor : "transparent" }}>
                <TabList>
                    <Tab>FAQ 관리</Tab>
                    <Tab>
                        검토 큐
                        {reviewCount > 0 ? <span className={styles.countRed} style={{ marginLeft : "6px" }}>{reviewCount}</span> : null}
                    </Tab>
                    <Tab>통계</Tab>
                </TabList>
            </Tabs>

            {sub === 0 && <FaqItems user={props.user} categories={categories} refreshKey={refreshKey} onEdit={(id : number) => openEditor({ mode : "edit", itemId : id })} onNew={() => openEditor({ mode : "new" })} onChanged={bumpRefresh} />}
            {sub === 1 && <FaqReview user={props.user} refreshKey={refreshKey} onEdit={(itemId : number, feedbackIds : number[]) => openEditor({ mode : "edit", itemId, feedbackIds })} onNew={(prefill : any, feedbackIds : number[]) => openEditor({ mode : "new", prefill, feedbackIds })} onChanged={bumpRefresh} />}
            {sub === 2 && <FaqStats user={props.user} refreshKey={refreshKey} goReview={() => setSub(1)} />}

            <FaqEditDrawer
                open={drawer.open}
                mode={drawer.mode}
                itemId={drawer.itemId}
                prefill={drawer.prefill}
                feedbackId={drawer.feedbackIds && drawer.feedbackIds.length > 0 ? drawer.feedbackIds[0] : undefined}
                categories={categories}
                onClose={() => setDrawer((d) => ({ ...d, open : false }))}
                onSaved={handleSaved}
                onDeleted={() => { setDrawer((d) => ({ ...d, open : false })); bumpRefresh(); }}
                onCategoriesChanged={loadCategories}
                goReview={() => { setDrawer((d) => ({ ...d, open : false })); setSub(1); }}
            />

            <Modal open={deployOpen} onClose={() => { if (!deploying) setDeployOpen(false); }}>
                <ModalDialog sx={{ padding : 0, borderRadius : "14px", width : "440px", maxWidth : "95vw" }}>
                    <div className={styles.modal} style={{ boxShadow : "none" }}>
                        <div className={styles.modalHeader}>배포하기</div>
                        <div className={styles.modalBody}>
                            <div className={styles.helper}>
                                미배포 변경 <b>{formatNumber(pending)}건</b>을 포함해 활성 FAQ 전체를 새 버전으로 묶습니다.
                                답변은 자르지 않고 전문을 그대로 넣고 토큰 수만 측정합니다. 배포 직후 캐시 워밍 1회가 나갑니다. 보통 10~20초 걸립니다.
                            </div>
                            <div className={styles.field}>
                                <div className={styles.label}>배포 메모 <span>(선택)</span></div>
                                <Textarea minRows={2} value={deployMemo} onChange={(e) => setDeployMemo(e.target.value)} placeholder="예: 11월 결제 항목 수정, 윈터 항목 3건 추가" disabled={deploying} />
                            </div>
                            {deploying ? <div className={styles.rowFlex} style={{ color : "#6B7280", fontSize : "13px" }}><CircularProgress size="sm" /> 빌드 중… count_tokens 측정 → 스냅샷 저장 → 캐시 워밍</div> : null}
                        </div>
                        <div className={styles.modalFooter}>
                            <button className={styles.btn} disabled={deploying} onClick={() => setDeployOpen(false)}>취소</button>
                            <button className={`${styles.btn} ${styles.btnPrimary}`} disabled={deploying} onClick={deploy}>배포</button>
                        </div>
                    </div>
                </ModalDialog>
            </Modal>
        </div>
    );
};

export default FaqAdminMain;
